import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import {
  MlRetrainingCandidateDto,
  MlRetrainingStatusDto,
  MlTrainingReadinessDto,
  MlTrainingRunResultDto
} from '../../../_core/_model/machine-learning';
import { AuthService } from '../../../_core/_services/auth.service';
import { MachineLearningAdminService } from '../../../_core/_services/machine-learning-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-ml-training',
  standalone: false,
  templateUrl: './admin-ml-training.html',
  styleUrl: './admin-ml-training.css'
})
export class AdminMlTraining implements OnInit {
  loading = false;
  actionLoading = false;
  error = '';
  isSuperAdmin = false;
  readiness: MlTrainingReadinessDto | null = null;
  retraining: MlRetrainingStatusDto | null = null;
  history: MlRetrainingCandidateDto[] = [];
  lastTrainingRun: MlTrainingRunResultDto | null = null;
  fromDate = '';
  toDate = '';
  version = '';
  forceRetraining = false;

  constructor(
    private mlService: MachineLearningAdminService,
    private auth: AuthService,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe(user => this.isSuperAdmin = user?.role === 'SuperAdmin');
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      readiness: this.mlService.getLoginRiskTrainingReadiness(this.dateQuery()),
      retraining: this.mlService.getLoginRiskRetrainingStatus(),
      history: this.mlService.getLoginRiskRetrainingHistory(20)
    }).subscribe({
      next: result => {
        this.readiness = result.readiness.data;
        this.retraining = result.retraining.data;
        this.history = result.history.data || [];
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.errorMessage(error, 'Unable to load training and retraining status.');
        this.loading = false;
      }
    });
  }

  refreshReadiness(): void {
    this.loading = true;
    this.mlService.getLoginRiskTrainingReadiness(this.dateQuery()).subscribe({
      next: response => {
        this.readiness = response.data;
        this.loading = false;
      },
      error: error => {
        this.notify.showError(this.errorMessage(error, 'Unable to refresh training readiness.'), 'Error');
        this.loading = false;
      }
    });
  }

  exportDataset(): void {
    this.actionLoading = true;
    this.mlService.exportLoginRiskTrainingDataset(this.dateQuery()).subscribe({
      next: response => {
        const blob = response.body;
        if (!blob) {
          this.notify.showWarning('The dataset export was empty.', 'No file returned');
          return;
        }
        const disposition = response.headers.get('content-disposition') || '';
        const match = /filename\*?=(?:UTF-8''|\")?([^\";]+)/i.exec(disposition);
        const filename = decodeURIComponent((match?.[1] || 'login-risk-training-dataset.csv').replace(/\"/g, ''));
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
        this.notify.showSuccess('The anonymized training dataset was downloaded.', 'Export complete');
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to export the training dataset.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  trainModel(): void {
    this.actionLoading = true;
    this.mlService.trainLoginRiskModel({
      version: this.version.trim() || null,
      ...this.dateQuery()
    }).subscribe({
      next: response => {
        this.lastTrainingRun = response.data;
        this.version = '';
        this.notify.showSuccess(response.message || 'ML.NET candidate trained and registered.', 'Training complete');
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to train the login-risk model.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  evaluateRetraining(): void {
    if (!this.isSuperAdmin) return;
    this.actionLoading = true;
    this.mlService.evaluateLoginRiskRetraining(this.forceRetraining).subscribe({
      next: response => {
        const result = response.data;
        this.notify.showSuccess(
          result.candidateWasTrained ? `Candidate ${result.candidate?.version || ''} was trained.` : result.action,
          'Retraining evaluation complete'
        );
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to evaluate automated retraining.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  statusClass(value?: string | null): string {
    const normalized = (value || '').toLowerCase();
    if (['recommendshadowcomparison', 'ready', 'healthy'].includes(normalized)) return 'text-bg-success';
    if (['donotadvancecandidate', 'blocked', 'critical'].includes(normalized)) return 'text-bg-danger';
    if (['inprogress', 'watch', 'attentionrequired'].includes(normalized)) return 'text-bg-warning text-dark';
    return 'text-bg-secondary';
  }

  private dateQuery(): { fromDate?: string | null; toDate?: string | null } {
    return { fromDate: this.fromDate || null, toDate: this.toDate || null };
  }

  private errorMessage(error: any, fallback: string): string {
    return error?.error?.error?.details || error?.error?.message || error?.message || fallback;
  }
}
